import React, { useEffect, Fragment, createContext, useContext } from "react";
import Helmet from "react-helmet";
import HeaderOne from "./Header";
import Footer from "./footer";
import UseScript from "./useScript";
import CustomCursor from "./global/CustomCursor";
import FounderIntentEngine from "./growth/FounderIntentEngine";
import { IoLogoWhatsapp } from "react-icons/io";
import "../style.css";

export const LayoutContext = createContext(false);
export const PageLocationContext = createContext("");

const Layout = ({
  pageTitle,
  title,
  pageDisc,
  description,
  pageKeyword,
  keywords,
  children,
  pageLink_Canonical,
  canonicalUrl,
  canonical,
  href,
  pathname,
  path,
  location,
  ogImage = "https://www.goafreet.com/assets/images/logo.png",
  pageImage,
  image,
  ogType = "website",
  schemaData = null,
  schemaMarkup = null,
  breadcrumbs = null,
  hideHeader = false,
  hideFooter = false,
}) => {
  const isNested = useContext(LayoutContext);
  const contextPath = useContext(PageLocationContext);
  
  const handleRadius = () => {
    if (typeof document === "undefined") return;
    const dynamicRadius = document.querySelectorAll(".dynamic-radius");
    dynamicRadius.forEach(function (btn) {
      let btnHeight = btn.offsetHeight;
      btn.style.borderBottomLeftRadius = btnHeight / 2 + "px";
      btn.style.borderTopLeftRadius = btnHeight / 2 + "px";
    });
  };

  const pathCandidate = pathname || path || (location && location.pathname) || contextPath;
  const isToolOrResource = Boolean(
    (pathCandidate && (
      pathCandidate.includes("/tools/") ||
      pathCandidate.includes("/resources/") ||
      pathCandidate.includes("/instagram-design")
    )) ||
    (typeof window !== "undefined" && (
      window.location.pathname.includes("/tools/") ||
      window.location.pathname.includes("/resources/") ||
      window.location.pathname.includes("/instagram-design")
    ))
  );

  useEffect(() => {
    handleRadius();
    if (typeof window !== "undefined") {
      const isTool = window.location.pathname.includes("/tools/") || 
                     window.location.pathname.includes("/resources/") || 
                     window.location.pathname.includes("/instagram-design");
      if (isTool) {
        document.body.classList.add("tool-page-active");
      } else {
        document.body.classList.remove("tool-page-active");
      }
    }
  }, []);

  // If this Layout is nested inside an existing Layout (e.g. embedded forms or widgets),
  // do NOT duplicate header, footer, or scripts. Only render the children!
  if (isNested) {
    return <Fragment>{children}</Fragment>;
  }

  // Resolve normalized props
  const resolvedTitle = pageTitle || title || "Goafreet - Web/App Development, UI/UX & Digital Marketing Company in India";
  const resolvedDescription = pageDisc || description || "Leading IT & Digital Marketing Services Company in India. We offer Web & App Development, UI/UX Design, Marketplace Operations (Amazon, Flipkart, Meesho, Quick Commerce), and Performance SEO.";
  const resolvedKeywords = pageKeyword || keywords || "it company in india, web development company india, digital marketing agency india, seo company india, marketplace management vadodara";
  const resolvedImage = pageImage || image || ogImage || "https://www.goafreet.com/assets/images/logo.png";
  const resolvedSchema = schemaData || schemaMarkup;

  let resolvedCanonical = pageLink_Canonical || canonicalUrl || canonical || href;
  if (!resolvedCanonical && pathCandidate) {
    const rawPath = pathCandidate.toString().trim();
    const cleanPath = rawPath.endsWith("/") ? rawPath : `${rawPath}/`;
    resolvedCanonical = `https://www.goafreet.com${cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`}`;
  }
  if (!resolvedCanonical && typeof window !== "undefined" && window.location) {
    const p = window.location.pathname || "/";
    const cleanPath = p.endsWith("/") ? p : `${p}/`;
    resolvedCanonical = `https://www.goafreet.com${cleanPath}`;
  }

  // Authoritative global Schema.org Organization + WebSite graph for India
  const baseSchemaGraph = [
    {
      "@type": ["Organization", "LocalBusiness", "ProfessionalService"],
      "@id": "https://www.goafreet.com/#organization",
      "name": "Goafreet",
      "alternateName": ["Goafreet Technologies", "Goafreet Digital"],
      "url": "https://www.goafreet.com/",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.goafreet.com/assets/images/logo.png",
        "caption": "Goafreet Logo"
      },
      "description": "India's premier digital service provider delivering high-performance Web & Mobile App Development, UI/UX Design, Marketplace Account Management, and Advanced SEO.",
      "telephone": "+91-9106981512",
      "email": "contact@goafreet.com",
      "priceRange": "₹₹",
      "currenciesAccepted": "INR, USD",
      "paymentAccepted": "Cash, Credit Card, UPI, Net Banking",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Vadodara",
        "addressRegion": "Gujarat",
        "postalCode": "390001",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 22.3072,
        "longitude": 73.1812
      },
      "areaServed": [
        {
          "@type": "Country",
          "name": "India"
        },
        {
          "@type": "AdministrativeArea",
          "name": "Pan-India"
        }
      ],
      "founder": {
        "@type": "Person",
        "name": "Gajendra Ojha",
        "url": "https://www.linkedin.com/in/gajendra-ojha"
      },
      "sameAs": [
        "https://twitter.com/goafreet",
        "https://www.linkedin.com/company/goafreet",
        "https://www.instagram.com/goafreet"
      ]
    },
    {
      "@type": "WebSite",
      "@id": "https://www.goafreet.com/#website",
      "name": "Goafreet",
      "url": "https://www.goafreet.com/",
      "inLanguage": "en-IN",
      "publisher": {
        "@id": "https://www.goafreet.com/#organization"
      },
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://www.goafreet.com/tools/?q={search_term_string}"
        },
        "query-input": "required name=search_term_string"
      }
    }
  ];

  if (breadcrumbs && Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
    baseSchemaGraph.push({
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "name": crumb.name,
        "item": crumb.url.startsWith("http") ? crumb.url : `https://www.goafreet.com${crumb.url.startsWith("/") ? "" : "/"}${crumb.url}`
      }))
    });
  }

  const completeSchema = {
    "@context": "https://schema.org",
    "@graph": baseSchemaGraph
  };

  // Only display calculation estimate disclaimer on financial/marketplace/metric calculators
  const isCalculationTool = resolvedCanonical && (
    resolvedCanonical.includes("/calculators/") ||
    resolvedCanonical.includes("-calculator") ||
    resolvedCanonical.includes("/marketplace/") ||
    resolvedCanonical.includes("/amazon/") ||
    resolvedCanonical.includes("/flipkart/") ||
    resolvedCanonical.includes("/meesho/") ||
    resolvedCanonical.includes("/quick-commerce/") ||
    resolvedCanonical.includes("/usa/")
  ) && !resolvedCanonical.includes("/design/") && !resolvedCanonical.includes("color") && !resolvedCanonical.includes("palette");

  return (
    <LayoutContext.Provider value={true}>
      <Fragment>
        <Helmet htmlAttributes={{ lang: "en-IN" }} title={resolvedTitle}>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
          <meta name="author" content="Gajendra Ojha" />
          <meta name="description" content={resolvedDescription} />
          <meta name="keywords" content={resolvedKeywords} />
          <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
          <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
          <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
          
          {/* Geo Targeting India */}
          <meta name="geo.region" content="IN-GJ" />
          <meta name="geo.placename" content="Vadodara, Gujarat, India" />
          <meta name="geo.position" content="22.3072;73.1812" />
          <meta name="ICBM" content="22.3072, 73.1812" />
          
          <base href="/" />
          <meta name="p:domain_verify" content="7b47996e145ed0dff5bd34ef46ea1b0a" />
          
          {/* Canonical & Hreflang */}
          {resolvedCanonical && <link rel="canonical" href={resolvedCanonical} />}
          {resolvedCanonical && <link rel="alternate" hrefLang="en-IN" href={resolvedCanonical} />}
          {resolvedCanonical && <link rel="alternate" hrefLang="x-default" href={resolvedCanonical} />}
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content={ogType} />
          <meta property="og:site_name" content="Goafreet" />
          <meta property="og:locale" content="en_IN" />
          <meta property="og:locale:alternate" content="en_US" />
          <meta property="og:title" content={resolvedTitle} />
          <meta property="og:description" content={resolvedDescription} />
          <meta property="og:url" content={resolvedCanonical} />
          {resolvedImage && <meta property="og:image" content={resolvedImage} />}

          {/* Twitter Cards */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@goafreet" />
          <meta name="twitter:creator" content="@goafreet" />
          <meta name="twitter:title" content={resolvedTitle} />
          <meta name="twitter:description" content={resolvedDescription} />
          {resolvedImage && <meta name="twitter:image" content={resolvedImage} />}

          {/* Structured Data / JSON-LD */}
          <script type="application/ld+json">
            {JSON.stringify(completeSchema)}
          </script>
          {resolvedSchema && (
            <script type="application/ld+json">
              {JSON.stringify(resolvedSchema)}
            </script>
          )}

          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
          <link 
            href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Lato:wght@400;700&display=swap" 
            rel="stylesheet" 
          />
        </Helmet>
        
        {!hideHeader && <HeaderOne />}

        <CustomCursor />

        <main style={{ fontFamily: "'Inter', 'Plus Jakarta Sans', 'Lato', sans-serif" }}>
          {children}
          {isCalculationTool && (
            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px', marginTop: '10px', marginBottom: '30px', textAlign: 'center', fontSize: '0.85rem', color: '#64748b' }}>
              <p>* Note: The metrics calculated by this tool are estimated numbers. Real numbers might vary according to the latest platform policies, taxes, and category updates.</p>
            </div>
          )}
        </main>
          
        {!hideFooter && <Footer />}
        <UseScript />

        {!isToolOrResource && (
          <a
            className="whatsapp-float"
            aria-label="WhatsApp"
            href="https://wa.me/9106981512"
            target="_blank"
            rel="noopener noreferrer"
          >
            <IoLogoWhatsapp className="my-float" />
          </a>
        )}

        <FounderIntentEngine />

      </Fragment>
    </LayoutContext.Provider>
  );
};

export default Layout;
